import React, { useState, useEffect } from "react";
import { bool, func, object } from "prop-types";
import {
  Form,
  Button,
  Modal,
  InputNumber,
  message,
  Input,
  Select,
  Divider,
  Collapse,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import checkAuth from "../../../utils/checkAuth";
import { useUpdateCampaign, useValidRulesTables } from "../../../api/campaigns";
import { useContractById } from "../../../api/contracts";
import { useProductPlatforms } from "../../../api/productPlatforms";
import { getAmazonAsinUrl } from "../../../utils/amazon";
import { isLoading as setLoading } from "../../../redux/shared/sharedActions";
import RetoolButton from "../../shared/RetoolButton/RetoolButton";
import MoneyInput from "../../shared/MoneyInput";
import SelectionGroupField from "../../shared/SelectionGroupField";
import ServiceFeeCalculator from "./ServiceFeeCalculator";
import CampaignPriceReceipt from "./CampaignPriceReceipt";
import { normalizeSelectionGroup } from "../../../utils/selectionGroups";
import {
  computeServiceFee,
  computeTotalPrice,
  buildServiceFeeDescription,
} from "../../../utils/campaignPricing";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const rowStyle = {
  display: "flex",
  gap: "24px",
  width: "100%",
  flexWrap: "wrap",
};
// Consistent three-column rhythm for the top form sections.
const thirdCol = { width: "calc(33.333% - 16px)", marginBottom: 8 };
// Blank field == default group, so only prefill a real, non-default handle.
const editableSelectionGroup = group => {
  const normalized = normalizeSelectionGroup(group);
  return normalized && normalized !== "default" ? group : undefined;
};
const priceCol = { width: "calc(50% - 12px)" };
const dividerStyle = { marginTop: 8, marginBottom: 12 };
// Fields nested in the collapsible pricing panel; used to auto-expand it when
// one of them fails validation on submit.
const PRICING_FIELDS = ["productReimbursement", "productServiceFees"];
export default function EditCampaignModal({ campaign, visible, onClose }) {
  const [calculatedServiceFees, setCalculatedServiceFees] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [receiptReimbursement, setReceiptReimbursement] = useState(0);
  const [receiptServiceFees, setReceiptServiceFees] = useState(0);
  const [receiptDescription, setReceiptDescription] = useState("");
  // Controlled so we can force the pricing panel open when it hides a validation error.
  const [pricingPanelKey, setPricingPanelKey] = useState([]);

  const dispatch = useDispatch();
  const updateCampaign = useUpdateCampaign(campaign?.campaignId);
  const { isLoading, isError, error, isSuccess } = updateCampaign;

  const [form] = Form.useForm();

  const product = campaign && campaign.relations.products;
  const productPrice =
    product &&
    product.relations.productPrices.find(p => p.price === product.defaultPrice);

  //redux
  const roles = useSelector(state => state.auth.roles);

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (campaign && product && productPrice) {
      form.setFieldsValue({
        productReimbursement: productPrice.productPrice,
        productServiceFees: productPrice.serviceFee,
        serviceFeeDescription: productPrice.serviceFeeDescription,
        target: campaign.target,
        state: campaign.state,
        rulesTable: campaign.rulesTable,
        sortOffset: campaign.sortOffset,
        featuredPriority: campaign.featuredPriority,
        featuredTag: campaign.featuredTag,
        selectionGroup: editableSelectionGroup(campaign.selectionGroup),
      });

      setTotalPrice(
        computeTotalPrice(productPrice.productPrice, productPrice.serviceFee)
      );
      setReceiptReimbursement(productPrice.productPrice);
      setReceiptServiceFees(productPrice.serviceFee);
      setReceiptDescription(productPrice.serviceFeeDescription || "");

      const { percentageOfProduct = 0, fixedFee = 0 } = form.getFieldsValue([
        "percentageOfProduct",
        "fixedFee",
      ]);
      if (productPrice.productPrice && (percentageOfProduct || fixedFee)) {
        setCalculatedServiceFees(
          computeServiceFee(
            productPrice.productPrice,
            percentageOfProduct,
            fixedFee
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  useEffect(() => {
    if (isError) {
      message.error(error?.message || "An error occurred");
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess) {
      form.resetFields();
      onClose();
      message.success("Campaign updated!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const { data: validRulesTables, isLoading: rulesTablesLoading } =
    useValidRulesTables();

  const { data: productPlatforms } = useProductPlatforms({});

  // Sibling campaigns share the contract, so their custom selection groups are
  // offered here for reuse (type "gold" on one campaign, pick it on the rest).
  // Includes/params match the contract page so this reuses its cached fetch and
  // satisfies flattenContracts (which needs companies + stats).
  const { data: contract } = useContractById({
    contractId: campaign?.contractId,
    includes: ["campaigns", "products", "companies"],
    params: { stats: true },
    enabled: visible && !!campaign?.contractId,
  });

  if (
    !campaign ||
    !product ||
    !productPrice ||
    !checkAuth.has(roles, ["admin"])
  ) {
    return null;
  }

  const productPlatform = (productPlatforms || []).find(
    p => p.productPlatformId === campaign.productPlatformId
  );
  const amazonUrl = getAmazonAsinUrl(productPlatform, campaign.code);

  const onSubmit = async () => {
    let values = null;
    try {
      values = await form.validateFields();
    } catch (err) {
      // A field may be hidden inside the collapsed pricing panel; expand it so
      // the error is visible, then scroll to the first offending field.
      const errorFields = err?.errorFields || [];
      const hasPricingError = errorFields.some(field =>
        field.name.some(name => PRICING_FIELDS.includes(name))
      );
      if (hasPricingError) {
        setPricingPanelKey(["pricing"]);
      }
      const [firstError] = errorFields;
      if (firstError) {
        // Defer so a just-expanded panel is mounted before we scroll to it.
        setTimeout(
          () => form.scrollToField(firstError.name, { block: "center" }),
          0
        );
      }
      return;
    }

    const putBody = {
      target: values.target,
      state: values.state,
      rulesTable: values.rulesTable,
      sortOffset: values.sortOffset,
      featuredPriority: values.featuredPriority,
      featuredTag: values.featuredTag,
      // Empty clears back to the DB default; backend re-validates + normalizes.
      selectionGroup:
        normalizeSelectionGroup(values.selectionGroup) || "default",
    };
    if (
      values.productReimbursement !== productPrice.productPrice ||
      values.productServiceFees !== productPrice.serviceFee ||
      values.serviceFeeDescription !== productPrice.serviceFeeDescription
    ) {
      //if value has been changed, update via relations
      putBody.relations = {
        products: {
          productId: campaign.productId,

          relations: {
            productPrices: {
              productPrice: values.productReimbursement,
              serviceFee: values.productServiceFees || 0.0,
              serviceFeeDescription: values.serviceFeeDescription,
            },
          },
        },
      };
    }

    updateCampaign.mutate({ campaignId: campaign.campaignId, body: putBody });
  };

  const onCancel = () => {
    form.resetFields();
    // Modal stays mounted while hidden, so derived pricing state must be
    // restored from productPrice to avoid leaking values from an abandoned edit.
    setCalculatedServiceFees(0);
    setTotalPrice(
      productPrice
        ? computeTotalPrice(productPrice.productPrice, productPrice.serviceFee)
        : 0
    );
    setReceiptReimbursement(productPrice ? productPrice.productPrice : 0);
    setReceiptServiceFees(productPrice ? productPrice.serviceFee : 0);
    setReceiptDescription(productPrice?.serviceFeeDescription || "");
    setPricingPanelKey([]);
    onClose();
  };

  const onValuesChange = (changedValues, allValues) => {
    const {
      productReimbursement,
      percentageOfProduct = 0,
      fixedFee = 0,
      productServiceFees,
      serviceFeeDescription,
    } = allValues;
    if (productReimbursement) {
      setCalculatedServiceFees(
        computeServiceFee(productReimbursement, percentageOfProduct, fixedFee)
      );
    }

    setReceiptDescription(serviceFeeDescription || "");

    if (productReimbursement) {
      setTotalPrice(
        computeTotalPrice(productReimbursement, productServiceFees)
      );
      setReceiptReimbursement(productReimbursement);
      setReceiptServiceFees(productServiceFees || 0);
    }
  };

  const applyCalculatedFee = () => {
    const { productReimbursement, percentageOfProduct, fixedFee } =
      form.getFieldsValue([
        "productReimbursement",
        "percentageOfProduct",
        "fixedFee",
      ]);
    const description = buildServiceFeeDescription(
      percentageOfProduct || 0,
      fixedFee || 0
    );
    form.setFieldsValue({
      productServiceFees: calculatedServiceFees,
      ...(description ? { serviceFeeDescription: description } : {}),
    });
    // setFieldsValue does not trigger onValuesChange, so update the receipt manually.
    setReceiptServiceFees(calculatedServiceFees);
    if (description) {
      setReceiptDescription(description);
    }
    if (productReimbursement) {
      setTotalPrice(
        computeTotalPrice(productReimbursement, calculatedServiceFees)
      );
    }
  };

  const copyCalculatedFee = async () => {
    try {
      await navigator.clipboard.writeText(String(calculatedServiceFees));
      message.success("Calculated fee copied");
    } catch (err) {
      message.error("Unable to copy");
    }
  };

  return (
    <Modal
      title={`Edit ${campaign.name} campaign`}
      width={isMobile ? "100%" : 880}
      visible={visible}
      onCancel={onCancel}
      footer={[
        <RetoolButton key="1" campaignId={`${campaign.campaignId}`} />,
        <Button key="2" disabled={isLoading} onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="3" type="primary" onClick={onSubmit} loading={isLoading}>
          Submit
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{
          productReimbursement: productPrice.productPrice,
          productServiceFees: productPrice.serviceFee,
          serviceFeeDescription: productPrice.serviceFeeDescription,
          percentageOfProduct: 10,
          target: campaign.target,
          state: campaign.state,
          rulesTable: campaign.rulesTable,
          sortOffset: campaign.sortOffset,
          featuredPriority: campaign.featuredPriority,
          featuredTag: campaign.featuredTag,
          selectionGroup: campaign.selectionGroup || "default",
        }}
        onValuesChange={onValuesChange}
      >
        <Divider orientation="left" style={{ ...dividerStyle, marginTop: 0 }}>
          Status & Targeting
        </Divider>
        <div style={rowStyle}>
          <Form.Item
            label="State"
            name="state"
            style={thirdCol}
            tooltip="The campaign's current status. Controls whether it's live and accepting influencers (active), winding down (closing), temporarily stopped (paused), finished (done), no longer eligible (disqualified), or awaiting approval (in review)."
          >
            <Select
              options={[
                {
                  value: "active",
                },
                {
                  value: "closing",
                },
                {
                  value: "paused",
                },
                {
                  value: "done",
                },
                {
                  value: "disqualified",
                },
                {
                  value: "in_review",
                  label: "in review",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Rules Table"
            name="rulesTable"
            style={thirdCol}
            tooltip="The set of eligibility rules that decides which influencers are allowed to apply to this campaign."
          >
            <Select
              showSearch
              disabled={!validRulesTables || rulesTablesLoading}
              options={validRulesTables
                ?.sort((a, b) => a.localeCompare(b))
                .map(table => {
                  const lowerTable = table.toLowerCase();
                  let color = "inherit";
                  if (lowerTable.includes("_top")) {
                    color = "darkgreen";
                  } else if (lowerTable.includes("_low")) {
                    color = "darkred";
                  }
                  return {
                    value: table,
                    label: <span style={{ color }}>{table}</span>,
                  };
                })}
            />
          </Form.Item>
          <Form.Item
            label="Target"
            name="target"
            style={thirdCol}
            tooltip="The goal number of influencers you want to sign up for this campaign."
          >
            <InputNumber min={0} allowClear style={{ width: "100%" }} />
          </Form.Item>
        </div>
        <div style={rowStyle}>
          <SelectionGroupField
            form={form}
            contractCampaigns={contract?.relations?.campaigns}
          />
        </div>

        <Divider orientation="left" style={dividerStyle}>
          Visibility & Ordering
        </Divider>
        <div style={rowStyle}>
          <Form.Item
            label="Featured Tag"
            name="featuredTag"
            style={thirdCol}
            tooltip="An optional badge shown on the campaign. Leave it blank for no badge."
          >
            <Select
              allowClear
              options={[
                {
                  label: "Popular",
                  value: "popular",
                },
                {
                  label: "New Arrival",
                  value: "new_arrival",
                },
                {
                  label: "Ending Soon",
                  value: "ending_soon",
                },
                {
                  label: "Video Contest",
                  value: "video_contest",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Featured Priority"
            name="featuredPriority"
            style={thirdCol}
            tooltip="A higher number pushes this campaign nearer the top of the featured section."
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Sort Offset"
            name="sortOffset"
            style={thirdCol}
            tooltip="A manual nudge that moves this campaign up or down in the listing order."
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Collapse
          style={{ marginTop: 16 }}
          activeKey={pricingPanelKey}
          onChange={setPricingPanelKey}
        >
          <Collapse.Panel
            key="pricing"
            // Ensures the panel content is rendered even when the panel is collapsed.
            // Otherwise the submission will fail due to missing price fields in the panel
            forceRender
            header={<span>Edit Product &amp; Pricing</span>}
          >
            <div style={rowStyle}>
              <Form.Item
                label="Product Reimbursement"
                name="productReimbursement"
                style={priceCol}
                rules={[
                  {
                    required: true,
                    message: "valid price is required",
                  },
                ]}
              >
                <MoneyInput />
              </Form.Item>
            </div>

            <Divider orientation="left" style={dividerStyle}>
              Service Fees
            </Divider>
            <div style={rowStyle}>
              {/* Left: the values that get saved and shown to the brand. */}
              <div style={{ flex: "1 1 340px" }}>
                <Form.Item
                  label="Product Service Fees"
                  name="productServiceFees"
                  style={{ marginBottom: 12 }}
                >
                  <MoneyInput />
                </Form.Item>
                <Form.Item
                  name="serviceFeeDescription"
                  label="Description"
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea autoSize={{ minRows: 4 }} />
                </Form.Item>
              </div>
              {/* Right: calculator tools to derive a suggested service fee. */}
              <ServiceFeeCalculator
                calculatedServiceFees={calculatedServiceFees}
                onApply={applyCalculatedFee}
                onCopy={copyCalculatedFee}
              />
            </div>
          </Collapse.Panel>
        </Collapse>

        <CampaignPriceReceipt
          reimbursement={receiptReimbursement}
          serviceFees={receiptServiceFees}
          description={receiptDescription}
          totalPrice={totalPrice}
          code={campaign.code}
          amazonUrl={amazonUrl}
        />
      </Form>
    </Modal>
  );
}

EditCampaignModal.propTypes = {
  campaign: object,
  visible: bool,
  onClose: func,
};
